import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Btn, Badge, Loading, EmptyState } from '../components/ui';

// ─── OB CARD ─────────────────────────────────────────────────────────────────
function ObCard({ ob, onCopy, onOpen, onLineDesign, onSaveTemplate }) {
  const statusColor = { Active: 'green', Draft: 'gray', Completed: 'blue' };
  return (
    <div
      style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer', transition: 'border-color 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--acc)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-md)'}
      onClick={() => onOpen(ob)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontFamily: 'monospace', marginBottom: 3 }}>{ob.ob_number}</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{ob.style_number}</div>
          {ob.style_description && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{ob.style_description}</div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
            Buyer: {ob.buyer} · {ob.season} · {ob.op_count || 0} ops
          </div>
        </div>
        <Badge type={statusColor[ob.status] || 'gray'}>{ob.status}</Badge>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, borderTop: '0.5px solid var(--border)' }}>
        <span style={{ fontSize: 13 }}>
          Total SAM: <strong style={{ color: 'var(--acc)' }}>{parseFloat(ob.total_sam || 0).toFixed(2)} min</strong>
        </span>
        <div style={{ display: 'flex', gap: 7 }} onClick={e => e.stopPropagation()}>
          <Btn size="sm" onClick={() => onCopy(ob.id)}>Copy</Btn>
          <Btn size="sm" onClick={() => onSaveTemplate(ob.id, ob.style_number)}>Save as template</Btn>
          <Btn size="sm" onClick={() => onOpen(ob)}>Open →</Btn>
          <Btn size="sm" variant="primary" onClick={() => onLineDesign(ob)}>Create line design →</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── OB EDITOR ───────────────────────────────────────────────────────────────
function ObEditor({ ob, operations, groups, machineTypes, onBack, onSaved }) {
  const [styleNumber, setStyleNumber] = useState(ob?.style_number || '');
  const [styleDescription, setStyleDescription] = useState(ob?.style_description || '');
  const [buyer, setBuyer] = useState(ob?.buyer || '');
  const [season, setSeason] = useState(ob?.season || '');
  const [status, setStatus] = useState(ob?.status || 'Draft');
  const [editorDivisions, setEditorDivisions] = useState([]);
  const [editorAllFactories, setEditorAllFactories] = useState([]);
  const [editorFactories, setEditorFactories] = useState([]);
  const [editorDivision, setEditorDivision] = useState('');
  const [factoryId, setFactoryId] = useState(ob?.factory_id || '');

  useEffect(() => {
    api.get('/divisions').then(r => setEditorDivisions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/factories').then(r => {
      const f = Array.isArray(r.data) ? r.data : [];
      setEditorAllFactories(f);
      setEditorFactories(f);
      // Pre-set division if ob has factory_id
      if (ob?.factory_id) {
        const fac = f.find(x => String(x.id) === String(ob.factory_id));
        if (fac) setEditorDivision(String(fac.division_id));
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (editorDivision) {
      setEditorFactories(editorAllFactories.filter(f => String(f.division_id) === String(editorDivision)));
    } else {
      setEditorFactories(editorAllFactories);
    }
  }, [editorDivision, editorAllFactories]);

  const [items, setItems] = useState(
    (ob?.items?.length
      ? ob.items
      : [{ _key: Date.now(), operation_id: '', operation_name: '', sam: '', machine_type: '', op_type: 'Machine', fundamental_group_id: '' }]
    ).map((it, i) => ({ ...it, _key: it._key || Date.now() + i }))
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);

  const totalSam = items.reduce((s, it) => s + parseFloat(it.sam || 0), 0);

  const selectOperation = (idx, opId) => {
    const op = operations.find(o => String(o.id) === String(opId));
    setItems(prev => {
      const next = [...prev];
      if (!op) {
        next[idx] = { ...next[idx], operation_id: '', operation_name: '', sam: '', machine_type: '' };
      } else if (op.op_type === 'Non-machine') {
        // Non-machine operations still use a single fixed SAM — unaffected by machine-SAM.
        next[idx] = {
          ...next[idx],
          operation_id: op.id,
          operation_name: op.name,
          sam: parseFloat(op.sam).toFixed(2),
          machine_type: '',
          op_type: op.op_type,
          fundamental_group_id: op.fundamental_group_id || '',
        };
      } else {
        // Machine-type operation: SAM depends on which machine is used.
        // Auto-pick when there's only one machine option; otherwise leave
        // blank until the user chooses a machine for this row.
        const machineSams = op.machine_sams || [];
        const single = machineSams.length === 1 ? machineSams[0] : null;
        next[idx] = {
          ...next[idx],
          operation_id: op.id,
          operation_name: op.name,
          sam: single ? parseFloat(single.sam).toFixed(2) : '',
          machine_type: single ? single.machine_type : '',
          op_type: op.op_type || 'Machine',
          fundamental_group_id: op.fundamental_group_id || '',
        };
      }
      return next;
    });
  };

  // Picking a machine for a row auto-fills the correct SAM for that specific
  // (operation, machine) combination — still manually editable afterward,
  // same as the existing "auto-filled but overridable" SAM behavior.
  const selectMachineForRow = (idx, machineType) => {
    setItems(prev => {
      const next = [...prev];
      const item = next[idx];
      const op = operations.find(o => String(o.id) === String(item.operation_id));
      const match = op?.machine_sams?.find(m => m.machine_type === machineType);
      next[idx] = { ...item, machine_type: machineType, sam: match ? parseFloat(match.sam).toFixed(2) : item.sam };
      return next;
    });
  };

  const addRow = (afterIdx) => {
    const newRow = { _key: Date.now() + Math.random(), operation_id: '', operation_name: '', sam: '', machine_type: '', op_type: 'Machine', fundamental_group_id: '' };
    setItems(prev => {
      if (afterIdx === undefined) return [...prev, newRow];
      const next = [...prev];
      next.splice(afterIdx + 1, 0, newRow);
      return next;
    });
  };

  const deleteRow = (idx) => {
    setItems(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleDragStart = (e, idx) => {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverIdx.current = idx;
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    const from = dragIdx.current;
    const to = idx;
    if (from === null || from === to) return;
    setItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    dragIdx.current = null;
    dragOverIdx.current = null;
  };

  const handleDragEnd = () => {
    dragIdx.current = null;
    dragOverIdx.current = null;
  };

  // ── EXPLICIT payload — style_description always included ──────────────────
  const handleSave = async (saveStatus) => {
    if (!styleNumber || !buyer || !season) { setError('Style number, buyer and season are required'); return; }
    const validItems = items.filter(it => it.operation_id && parseFloat(it.sam) > 0);
    if (validItems.length === 0) { setError('Add at least one operation with a valid SAM'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        style_number: styleNumber,
        style_description: styleDescription,   // always explicit
        buyer: buyer,
        season: season,
        factory_id: factoryId || null,
        status: saveStatus || status,
        items: validItems.map((it, i) => ({
          row_number: i + 1,
          operation_id: it.operation_id,
          operation_name: it.operation_name,
          sam: parseFloat(it.sam),
          machine_type: it.machine_type || null,
          op_type: it.op_type || 'Machine',
          fundamental_group_id: it.fundamental_group_id || null,
        }))
      };
      if (ob?.id) await api.put(`/ob/${ob.id}`, payload);
      else await api.post('/ob', payload);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Error saving');
    }
    setSaving(false);
  };

  const machineOptions = [
    { value: '', label: '—' },
    ...(machineTypes || []).map(m => ({ value: m.machine_type, label: m.machine_type }))
  ];

  const selStyle = { width: '100%', padding: '4px 6px', fontSize: 11.5, border: '0.5px solid var(--border-md)', borderRadius: 5, background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'inherit' };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Btn size="sm" onClick={onBack}>← Back</Btn>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>
            {ob?.ob_number || 'New OB'}
            {ob?.ob_number && <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: 13 }}> · {styleNumber}</span>}
          </div>
        </div>
        <Btn size="sm" onClick={() => handleSave('Draft')} disabled={saving}>Save draft</Btn>
        <Btn size="sm" variant="primary" onClick={() => handleSave('Active')} disabled={saving}>
          {saving ? 'Saving…' : 'Save & activate →'}
        </Btn>
      </div>

      <div style={{ background: 'var(--bg-secondary)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Style number</div>
            <input className="inp" value={styleNumber} onChange={e => setStyleNumber(e.target.value)} placeholder="e.g. ST-4521" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Style description</div>
            <input className="inp" value={styleDescription} onChange={e => setStyleDescription(e.target.value)} placeholder="e.g. Basic crew neck tee" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Buyer</div>
            <input className="inp" value={buyer} onChange={e => setBuyer(e.target.value)} placeholder="e.g. H&M" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Season</div>
            <input className="inp" value={season} onChange={e => setSeason(e.target.value)} placeholder="e.g. Summer 2026" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Division</div>
            <select className="inp" value={editorDivision} onChange={e => { setEditorDivision(e.target.value); setFactoryId(''); }} style={{ width: '100%' }}>
              <option value="">Select division</option>
              {editorDivisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Factory</div>
            <select className="inp" value={factoryId} onChange={e => setFactoryId(e.target.value)} style={{ width: '100%' }}>
              <option value="">Select factory</option>
              {editorFactories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--red)', background: 'var(--red-bg)', padding: '7px 12px', borderRadius: 7 }}>{error}</div>
      )}

      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680, fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                {['', '#', 'Operation name', 'SAM', 'Machine', 'Op. type', 'Fund. group', ''].map((h, i) => (
                  <th key={i} style={{ padding: '7px 8px', borderBottom: '0.5px solid var(--border-md)', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 10, textAlign: i === 1 ? 'center' : 'left', width: [28, 32, undefined, 72, 100, 110, 130, 56][i] }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr
                  key={item._key}
                  draggable={true}
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDrop={e => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  style={{ borderBottom: '0.5px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FAFAF8'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ textAlign: 'center', padding: '4px 4px', color: 'var(--text-tertiary)', fontSize: 14, cursor: 'grab', userSelect: 'none' }}>⠿</td>
                  <td style={{ textAlign: 'center', fontSize: 10.5, color: 'var(--text-tertiary)', padding: '4px 4px' }}>{idx + 1}</td>

                  <td style={{ padding: '4px 6px' }}>
                    <select
                      value={item.operation_id || ''}
                      onChange={e => selectOperation(idx, e.target.value)}
                      style={{ ...selStyle, color: item.operation_id ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
                    >
                      <option value="">— select operation —</option>
                      {operations.map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </td>

                  <td style={{ padding: '4px 6px' }}>
                    <input
                      type="number"
                      value={item.sam}
                      onChange={e => updateRow(idx, 'sam', e.target.value)}
                      style={{ width: '100%', padding: '4px 7px', fontSize: 12, border: '0.5px solid var(--border-md)', borderRadius: 5, background: item.sam ? 'var(--acc-bg)' : 'var(--bg-primary)', color: 'var(--acc)', fontWeight: 500, fontFamily: 'inherit' }}
                    />
                  </td>

                  <td style={{ padding: '4px 6px' }}>
                    {(() => {
                      const op = operations.find(o => String(o.id) === String(item.operation_id));
                      const isNonMachine = op?.op_type === 'Non-machine';
                      const rowOptions = (op && !isNonMachine && op.machine_sams?.length)
                        ? [{ value: '', label: '— select —' }, ...op.machine_sams.map(m => ({ value: m.machine_type, label: m.machine_type }))]
                        : machineOptions;
                      return (
                        <select
                          value={item.machine_type || ''}
                          onChange={e => selectMachineForRow(idx, e.target.value)}
                          disabled={isNonMachine}
                          style={{ ...selStyle, opacity: isNonMachine ? 0.5 : 1 }}
                        >
                          {rowOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      );
                    })()}
                  </td>

                  <td style={{ padding: '4px 6px' }}>
                    <select value={item.op_type || 'Machine'} onChange={e => updateRow(idx, 'op_type', e.target.value)} style={selStyle}>
                      <option value="Machine">Machine</option>
                      <option value="Non-machine">Non-machine</option>
                    </select>
                  </td>

                  <td style={{ padding: '4px 6px' }}>
                    <select value={item.fundamental_group_id || ''} onChange={e => updateRow(idx, 'fundamental_group_id', e.target.value)} style={selStyle}>
                      <option value="">— select —</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </td>

                  <td style={{ padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <span
                      onClick={() => addRow(idx)}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, border: '0.5px dashed var(--border-md)', cursor: 'pointer', fontSize: 13, color: 'var(--text-tertiary)', marginRight: 4 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--acc)'; e.currentTarget.style.color = 'var(--acc)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-md)'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    >+</span>
                    <span
                      onClick={() => deleteRow(idx)}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, cursor: 'pointer', fontSize: 12, color: 'var(--text-tertiary)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--red-bg)'; e.currentTarget.style.color = 'var(--red)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    >✕</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', borderTop: '0.5px solid var(--border-md)', background: 'var(--bg-secondary)' }}>
          <button onClick={() => addRow()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--acc)', fontFamily: 'inherit', fontWeight: 500 }}>
            + Add row
          </button>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{items.filter(i => i.operation_id).length} operations · Total SAM</span>
            <span style={{ fontSize: 20, fontWeight: 500, color: 'var(--acc)' }}>{totalSam.toFixed(2)}</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>min</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function OperationBulletin() {
  const [obs, setObs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateModal, setTemplateModal] = useState({ open: false, obId: null, obName: '' });
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [operations, setOperations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [factories, setFactories] = useState([]);
  const [allFactories, setAllFactories] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedFactory, setSelectedFactory] = useState('');
  const [view, setView] = useState('list');
  const [activeOb, setActiveOb] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Operation bulletin'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [obRes, opsRes, grpsRes, machRes] = await Promise.all([
        api.get('/ob'),
        api.get('/operations'),
        api.get('/fundamental-groups'),
        api.get('/machine-types'),
      ]);
      setObs(obRes.data);
      setOperations(opsRes.data);
      setGroups(grpsRes.data);
      setMachineTypes(machRes.data);
    } catch (e) { console.error(e); }
    // Fetch divisions and factories separately so a failure doesn't block the rest
    try {
      const divRes = await api.get('/divisions');
      setDivisions(Array.isArray(divRes.data) ? divRes.data : []);
    } catch (e) { console.error('divisions fetch error', e); }
    try {
      const facRes = await api.get('/factories');
      const facData = Array.isArray(facRes.data) ? facRes.data : [];
      setFactories(facData);
      setAllFactories(facData);
    } catch (e) { console.error('factories fetch error', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (selectedDivision) {
      setFactories(allFactories.filter(f => String(f.division_id) === String(selectedDivision)));
      setSelectedFactory('');
    } else {
      setFactories(allFactories);
    }
  }, [selectedDivision, allFactories]);

  const handleOpen = async (ob) => {
    try {
      const { data } = await api.get(`/ob/${ob.id}`);
      setActiveOb(data);
      setView('editor');
    } catch (e) { alert('Error loading OB'); }
  };

  const loadTemplates = async (factoryId) => {
    try {
      const tRes = await api.get('/ob/templates' + (factoryId ? `?factory_id=${factoryId}` : ''));
      setTemplates(Array.isArray(tRes.data) ? tRes.data : []);
    } catch (e) { console.error(e); }
  };

  // Load templates whenever factory changes
  useEffect(() => { loadTemplates(selectedFactory); }, [selectedFactory]); // eslint-disable-line

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      await api.post(`/ob/${templateModal.obId}/save-as-template`, { name: templateName.trim() });
      setTemplateModal({ open: false, obId: null, obName: '' });
      setTemplateName('');
      await loadTemplates(selectedFactory);
    } catch (e) { console.error(e); }
    setSavingTemplate(false);
  };

  const handleUseTemplate = async (template) => {
    try {
      const { data } = await api.get(`/ob/templates/${template.id}`);
      const newOb = {
        id: null,
        ob_number: '(New)',
        style_number: '',
        buyer: '',
        season: '',
        factory_id: template.factory_id,
        items: (data.items || []).map((item, idx) => ({ ...item, id: null, _key: idx }))
      };
      setShowTemplates(false);
      setTimeout(() => {
        setActiveOb(newOb);
        setView('editor');
      }, 50);
    } catch (e) { console.error(e); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/ob/templates/${id}`);
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  const handleNew = () => { setActiveOb(null); setView('editor'); };

  const handleCopy = async (id) => {
    try { await api.post(`/ob/${id}/copy`); fetchAll(); }
    catch (e) { alert('Error copying OB'); }
  };

  const handleSaved = () => { setView('list'); fetchAll(); };

  const handleLineDesign = (ob) => {
    navigate('/line-design', { state: { ob_id: ob.id, ob_number: ob.ob_number, style_number: ob.style_number } });
  };

  const filtered = obs.filter(ob => {
    if (selectedDivision && ob.division_id && String(ob.division_id) !== String(selectedDivision)) return false;
    if (selectedFactory && ob.factory_id && String(ob.factory_id) !== String(selectedFactory)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      ob.style_number?.toLowerCase().includes(q) ||
      ob.buyer?.toLowerCase().includes(q) ||
      ob.style_description?.toLowerCase().includes(q)
    );
  });

  if (view === 'editor') {
    return <ObEditor ob={activeOb} operations={operations} groups={groups} machineTypes={machineTypes} onBack={() => setView('list')} onSaved={handleSaved} />;
  }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Operation bulletins <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{obs.length} records</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Style-wise operation breakdowns with SAM values</div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <select value={selectedDivision} onChange={e => setSelectedDivision(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
            <option value="">All divisions</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={selectedFactory} onChange={e => setSelectedFactory(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
            <option value="">All factories</option>
            {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      
      <input className="inp" placeholder="Search style / description / buyer…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 240 }} />
        <Btn variant="primary" size="sm" onClick={handleNew}>+ Create OB</Btn>
        <button onClick={() => setShowTemplates(true)} style={{ padding: '6px 14px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--acc)' }}>
          📋 Templates {templates.length > 0 ? `(${templates.length})` : ''}
        </button>
      </div>

      {loading ? <Loading /> : filtered.length === 0
        ? <EmptyState message="No operation bulletins yet" action={<Btn variant="primary" onClick={handleNew}>+ Create OB</Btn>} />
        : filtered.map(ob => (
          <ObCard key={ob.id} ob={ob} onCopy={handleCopy} onOpen={handleOpen} onLineDesign={handleLineDesign} onSaveTemplate={(id, name) => setTemplateModal({ open: true, obId: id, obName: name })} />
        ))
      }

      {/* ── Save as Template modal ── */}
      {templateModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Save as template</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Saving <strong>{templateModal.obName}</strong> as a reusable template
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase' }}>Template name</div>
            <input
              className="inp" autoFocus
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              placeholder="e.g. Basic T-Shirt, Polo Collar…"
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setTemplateModal({ open: false, obId: null, obName: '' }); setTemplateName(''); }}
                style={{ padding: '7px 16px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={handleSaveAsTemplate} disabled={!templateName.trim() || savingTemplate}
                style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: templateName.trim() ? 'var(--acc)' : 'var(--border)', color: '#fff', cursor: templateName.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                {savingTemplate ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Templates drawer ── */}
      {showTemplates && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 420, background: '#fff', height: '100%', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 20px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>OB Templates</div>
              <button onClick={() => setShowTemplates(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              {templates.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: 13 }}>
                  No templates yet. Open an OB and click "Save as template".
                </div>
              )}
              {templates.map(t => (
                <div key={t.id} style={{ background: 'var(--bg-tertiary)', borderRadius: 8, padding: '12px 14px', marginBottom: 10, border: '0.5px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {t.factory_name || 'No factory'} · {t.op_count || 0} operations
                      </div>
                    </div>
                    <button onClick={() => handleDeleteTemplate(t.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 14, padding: '2px 6px' }}
                      title="Delete template">✕</button>
                  </div>
                  <button onClick={() => handleUseTemplate(t)}
                    style={{ width: '100%', padding: '7px 0', borderRadius: 6, border: 'none', background: 'var(--acc)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Use this template →
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}